import { createAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { uploadFile } from './services';
import {
  ProgressUpdate,
  FileUploadWithId,
  UploadFileResponse,
} from './typings';
import localization from 'localization';

export const startUpload = createAction<FileUploadWithId>('START_UPLOAD');
export const updateUploadProgress = createAction<ProgressUpdate>(
  'UPDATE_UPLOAD_PROGRESS',
);
export const finishUpload = createAction('FINISH_UPLOAD');

export const uploadFiles = async (
  files: FileUploadWithId[],
  dispatch: any,
): Promise<UploadFileResponse[]> => {
  let fileIds: string[] = [];
  const fileUrls: UploadFileResponse[] = await Promise.all(
    files.map(async ({ file, id, appletId }) => {
      fileIds.push(id);
      dispatch(
        startUpload({
          appletId,
          id,
          file,
        }),
      );
      try {
        const response = await uploadFile({ file, id, appletId }, (p) =>
          dispatch(updateUploadProgress({ id, progress: p })),
        );

        return {
          ...response,
          fileType: file.type,
        };
      } catch (error: any) {
        console.log({ error });
        const isNetworkError = error?.message?.startsWith('NETWORK_ERROR:');
        const isSizeError = error?.message?.includes('too large') || 
                           error?.message?.includes('exceeds the maximum size');
        
        let errorMessage: string;
        if (isNetworkError) {
          errorMessage = error.message.replace('NETWORK_ERROR: ', '');
        } else if (isSizeError) {
          errorMessage = localization.fileUploadExceedsMaxSize;
        } else {
          errorMessage = 'Unable to upload file. Please try again.';
        }
        
        // Update progress with error information
        dispatch(updateUploadProgress({ 
          id, 
          progress: 0, 
          failed: true,
          errorMessage,
          isNetworkError,
        }));
        
        return {
          id,
          appletId,
          fileUrl: '',
          failed: true,
          errorMessage,
          isNetworkError,
        };
      }
    }),
  );
  const fileUrlsMap = fileUrls.reduce((acc, fileData) => {
    return {
      ...acc,
      [fileData.id]: fileData,
    };
  }, {} as Record<string, UploadFileResponse>);
  fileIds.forEach((fileId) => {
    const progress = fileUrlsMap[fileId]?.failed ? 0 : 100;
    dispatch(
      updateUploadProgress({
        id: fileId,
        progress,
        failed: fileUrlsMap[fileId]?.failed,
      }),
    );
  });
  
  // Check if all uploads were successful
  const allSuccessful = fileUrls.every((fileData) => !fileData.failed && fileData.fileUrl);
  if (allSuccessful && fileUrls.length > 0) {
    toast.success(localization.fileUploadSuccess, {
      position: 'top-center',
      autoClose: 3000,
    });
  }
  
  dispatch(finishUpload());
  return fileUrls;
};
